import { HttpClient, HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  NotificationMessageIcon,
  RetryServices,
} from "@app/framework/notification-sidebar/notification-sidebar.component";
import { environment } from "@env/environment";
import { HttpUtilService } from "@services/http-util.service";
import { NotificationsService } from "@services/notifications.service";
import { WebsocketService } from "@services/websocket.service";
import random from "lodash/random";
import cloneDeep from 'lodash/cloneDeep';
import { TranslationService } from "@services/translation.service";

@Injectable({
  providedIn: "root",
})
export class ZipService {
  public foldersToZip = {};
  constructor(
    private notifications: NotificationsService,
    public websocketService: WebsocketService,
    public http: HttpUtilService,
    private httpClient: HttpClient,
    private translationService:TranslationService
  ) {}

  startConnectionTimer(folder) {
    setTimeout(() => {
      if (
        !this.foldersToZip[folder.id || folder] ||
        this.foldersToZip[folder.id || folder].socketConnectionMade
      )
        return;
      this.handleGenericError(folder);
    }, 10000);
  }

  async zipFiles(prefix, files, folders, avoidPopups?) {
    const id = String(random(1, 999999));
    const isSingleFile = files?.length === 1 && !(folders?.length >= 1);

    if (!avoidPopups) {
      this.notifications.addUserNotification(
        {
          icon: NotificationMessageIcon.fileZip,
          text: `${this.translationService.getTranslatedContent("preparing","Preparing")} ${
            isSingleFile ? files?.[0]?.title : this.translationService.getTranslatedContent("zip","Zip")
          } ${this.translationService.getTranslatedContent("to_be_downloaded","to be downloaded")}`,
          progress: "0",
          preventRemoval: true,
          shouldForceOpen: true,
          status: `0%`,
        },
        id
      );
    }

    this.startConnectionTimer(id);
    const socket = this.websocketService.createSocket();

    this.foldersToZip[id] = { id, socket, files, folders, prefix };

    socket.onopen = (data) => {
      socket.send(
        JSON.stringify({
          action: "setlistener",
          data: {
            type: "data",
            "user-pool-id": environment.cognitoUserPoolId,
            "client-id": environment.cognitoClientId,
            "sub-domain": environment.domain,
          },
        })
      );
    };

    socket.onmessage = (event) => {
      const data = event.data.replace(/'/g, '"');
      const message = JSON.parse(data);
      if (message?.Status === "Listener set.") {
        this.foldersToZip[id].socketId = message["Connection"];

        this.startZipFileProcess(id);
        return;
      }

      if (message?.message === "Internal server error") {
        if (!message?.connectionId) {
          return this.handleGenericError("Zip");
        }

        this.foldersToZip[id].socketId = message["connectionId"];

        this.startZipFileProcess(id);
        return;
      }

        if (message?.Status === "initial connnection") {
          this.foldersToZip[id].socketConnectionMade = true;
          return;
        }
        if (message.progress && !avoidPopups) {
          this.foldersToZip[id].socketConnectionMade = true;
          this.notifications.addUserNotification(
            {
              text: `${this.translationService.getTranslatedContent('preparing_zip_for_download',"Preparing Zip for download")}`,
              progress: message.progress,
              preventRemoval: true,
              status: `${message.progress}%`,
              icon: NotificationMessageIcon.fileZip,
            },
            id
          );
        }

        // this.notifications.addUserNotification(
        //   {
        //     text: `Preparing Zip for download`,
        //     progress: message.progress,
        //     preventRemoval: true,
        //     status: `${message.progress}%`,
        //     icon: NotificationMessageIcon.fileZip,
        //   },
        //   String(id)
        // );
      // }

      if (message?.signedUrls) {
        this.foldersToZip[id].socketConnectionMade = true;
        if (message?.skipZip) {
          return this.downloadFile(message?.signedUrls[0], id);
        }

        message?.signedUrls.forEach((signedUrl) => {
          setTimeout(() => {
            this.downloadZip(signedUrl, id);
          }, 750);
        });
      }

      if (message?.error || !message) {
        if (!avoidPopups) return;
        const text = this.translationService.getTranslatedContent("no_files_to_zip","No Files to Zip");
        if (message.error === "No Files to Zip") {
          this.notifications.popNotification("", text);
        }
        this.handleGenericZipError(id);
      }
    };

    socket.onerror = (error) => {
      this.handleGenericZipError(id);
    };
  }

  async zipFolder(folder, prefix?) {
    if (this.foldersToZip[folder.id]) return;
    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fileZip,
        text: `${this.translationService.getTranslatedContent('preparing',"Preparing")} <b>${folder.data.title}</b> ${this.translationService.getTranslatedContent('for_download',"for download")}`,
        progress: "0",
        preventRemoval: true,
        shouldForceOpen: true,
        status: `0%`,
      },
      folder.id
    );
    this.startConnectionTimer(folder);
    const socket = this.websocketService.createSocket();

    this.foldersToZip[folder.id] = { folder, socket, prefix };

    socket.onopen = (data) => {
      socket.send(
        JSON.stringify({
          action: "setlistener",
          data: {
            type: "data",
            "user-pool-id": environment.cognitoUserPoolId,
            "client-id": environment.cognitoClientId,
            "sub-domain": environment.domain,
          },
        })
      );
    };

    socket.onmessage = (event) => {
      const data = event.data.replace(/'/g, '"');
      const message = JSON.parse(data);
      if (message?.Status === "Listener set.") {
        this.foldersToZip[folder.id].socketId = message["Connection"];

        this.startZipProcess(folder.id);
        return;
      }

      if (message?.message === "Internal server error") {
        if (!message?.connectionId) return this.handleGenericError(folder);

        this.foldersToZip[folder.id].socketId = message["connectionId"];

        this.startZipProcess(folder.id);
        return;
      }

      if (message?.Status === "initial connnection") {
        this.foldersToZip[folder.id].socketConnectionMade = true;
        return;
      }
      if (message.progress) {
        this.foldersToZip[folder.id].socketConnectionMade = true;

        this.notifications.addUserNotification(
          {
            text: `${this.translationService.getTranslatedContent('preparing',"Preparing")} <b>${folder.data.title}</b> ${this.translationService.getTranslatedContent('for_download',"for download")}`,
            progress: message.progress,
            preventRemoval: true,
            status: `${message.progress}%`,
            icon: NotificationMessageIcon.fileZip,
          },
          folder.id
        );
      }

      if (message?.signedUrls) {
        this.foldersToZip[folder.id].socketConnectionMade = true;

        message?.signedUrls.forEach((signedUrl, i) => {
          setTimeout(() => {
            this.downloadZipFile(signedUrl, folder, i + 1, prefix);
          }, 750);
        });
      }

      if (message?.error || !message) {
        if (message.error === "No Files to Zip") {
          const text = this.translationService.getTranslatedContent("no_files_to_zip","No Files to Zip");
          this.notifications.popNotification("",text);
        }
        this.handleGenericError(folder);
      }
    };

    socket.onerror = (error) => {
      this.handleGenericError(folder);
    };
  }

  startZipFileProcess(id) {
    const { socketId, files, folders, prefix } = this.foldersToZip[id];
    const skipZip = files?.length === 1 && !folders?.length;
    const url = `/api/download/zip-init?connectionId=${socketId}&skipZip=${skipZip}`;
    this.http
      .request("POST", url, { files, folders, prefix }, true, true)
      .subscribe(
        () => {},
        (err) => {
          this.handleGenericZipError(id);
        }
      );
  }

  startZipProcess(id) {
    const { socketId, folder, prefix } = this.foldersToZip[id];
    const url = `/api/download/zip-init?id=${id}&connectionId=${socketId}&prefix=${prefix}`;

    this.http.request("GET", url, null, true, true).subscribe(
      () => {},
      (err) => {
        this.handleGenericError(folder);
      }
    );
  }

  handleGenericZipError(id) {
    // hopefully avoids circular dependency
    const data = cloneDeep(this.foldersToZip[id]);
    data?.files?.forEach((file) => delete file?.route);
    data?.folders?.forEach((folder) => delete folder?.route);

    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fail,
        retry: {
          service: RetryServices.zipWithFilesAndFolders,
          data,
        },
        text: this.translationService.getTranslatedContent('preparing_file_for_download_has_failed',`Preparing File for download has failed`),
        status: this.translationService.getTranslatedContent('download_failed',"Download Failed"),

      },
      id
    );
    if (this.foldersToZip[id]?.socket) {
      this.foldersToZip[id]?.socket?.close();
    }
    delete this.foldersToZip[id];
  }

  handleGenericError(folder) {
    // hopefully avoids circular dependency
    const data = cloneDeep(folder);
    delete data.route;
    this.notifications.addUserNotification(
      {
        icon: NotificationMessageIcon.fail,
        retry: {
          service: RetryServices.zip,
          data,
        },
        text: this.translationService.getTranslatedContent('preparing_file_for_download_has_failed',`Preparing File for download has failed`) +  `<b>${folder.data.title}</b>`,        
        status: this.translationService.getTranslatedContent('download_failed',"Download Failed"),
      },
      folder.id
    );
    if (this.foldersToZip[folder.id]?.socket) {
      this.foldersToZip[folder.id]?.socket?.close();
    }
    delete this.foldersToZip[folder.id];
  }

  downloadFile(signedUrl: string, id) {
    const fileName = this.foldersToZip[id]?.files[0]?.title;

    this.httpClient
      .get(signedUrl, {
        responseType: "blob",
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (res) => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fileZip,
                text: `${this.translationService.getTranslatedContent("downloading","Downloading")} ${fileName}`,
                progress: percentDone,
                status: `${this.translationService.getTranslatedContent("downloading","Downloading")}`
              },
              id
            );
          }
          if (res.type === HttpEventType.Response) {
            res.body;
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";
            const url = window.URL.createObjectURL(res.body);
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);

            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: `${this.translationService.getTranslatedContent("downloading","Downloading")} ${fileName}`,
                progress: 100,
                status: `${this.translationService.getTranslatedContent("download_complete","Download Complete")}`
              },
              id
            );
            this.foldersToZip[id]?.socket?.close();
            delete this.foldersToZip[id];
          }
        },
        (err) => {
          this.handleGenericZipError(id);
        }
      );
  }

  downloadZip(signedUrl: string, id) {
    this.httpClient
      .get(signedUrl, {
        responseType: "blob",
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (res) => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fileZip,
                text: this.translationService.getTranslatedContent("downloading_zip","Downloading Zip"),
                progress: percentDone,
                status: "Downloading",
              },
              id
            );
          }
          if (res.type === HttpEventType.Response) {
            const blob = new Blob([res.body], {
              type: "application/zip",
            });
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = "zip-file" + ".zip";
            a.click();
            window.URL.revokeObjectURL(url);

            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: this.translationService.getTranslatedContent("downloading_zip","Downloading Zip"),
                progress: 100,
                status: "Download Complete",
              },
              id
            );
            this.foldersToZip[id]?.socket?.close();
            delete this.foldersToZip[id];
          }
        },
        (err) => {
          this.handleGenericZipError(id);
        }
      );
  }

  downloadZipFile(signedUrl: string, folder, index?, prefix?) {
    this.httpClient
      .get(signedUrl, {
        responseType: "blob",
        reportProgress: true,
        observe: "events",
      })
      .subscribe(
        (res) => {
          if (res.type === HttpEventType.DownloadProgress) {
            const percentDone = Math.round((100 * res.loaded) / res.total);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.fileZip,
                text: `${this.translationService.getTranslatedContent("downloading","Downloading")} <b>${folder.data.title}</b>`,
                progress: percentDone,
                status: "Downloading",
              },
              folder.id
            );
          }
          if (res.type === HttpEventType.Response) {
            const blob = new Blob([res.body], {
              type: "application/zip",
            });
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";
            const url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download =
              (`${prefix ? `${prefix}-` : ""}${folder?.data?.title}-${index}` ||
                "zip-file") + ".zip";
            a.click();
            window.URL.revokeObjectURL(url);
            this.notifications.addUserNotification(
              {
                icon: NotificationMessageIcon.checkmark,
                text: `${this.translationService.getTranslatedContent("downloading","Downloading")} <b>${folder?.data?.title||this.translationService.getTranslatedContent("file","File")}</b>`,
                progress: 100,
                status: "Download Complete",
              },
              folder.id
            );
            // }
            this.foldersToZip[folder.id]?.socket?.close();
            delete this.foldersToZip[folder.id];
          }
        },
        (err) => {
          this.handleGenericError(folder);
        }
      );
  }
}
